import { useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Box, IconButton, TextField } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";

const isArabic = (text: string) => /[\u0600-\u06FF]/.test(text);

const GinoChatInput = ({ disabled, onSend, placeholder }: { disabled: boolean; onSend: (text: string) => void; placeholder?: string }) => {
  const { t } = useTranslation("gino", { keyPrefix: "chatPage" });
  const [value, setValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSend = useCallback(() => {
    const text = value.trim();
    if (!text || disabled) return;
    onSend(text);
    setValue("");
  }, [value, disabled, onSend]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Box sx={styles.container}>
      <Box sx={styles.inputBox}>
        <TextField
          fullWidth
          multiline
          maxRows={4}
          placeholder={placeholder || t("messagePlaceholder")}
          value={value}
          disabled={disabled}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          inputRef={inputRef}
          sx={{ ...styles.input, ...(isArabic(value) ? { direction: "rtl" } : {}) }}
        />
        <IconButton sx={styles.sendBtn} disabled={disabled || !value.trim()} onClick={handleSend}>
          <SendIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
      <Box sx={styles.hint}>{t("accuracyHint")}</Box>
    </Box>
  );
};

export default GinoChatInput;

const styles = {
  container: { flexShrink: 0 },
  inputBox: { display: "flex", alignItems: "flex-end", background: "#fff", border: "1.5px solid #e5e7eb", borderRadius: "18px", padding: "8px 8px 8px 16px", boxShadow: "0 1px 2px rgba(16,24,40,0.04), 0 8px 24px -14px rgba(16,24,40,0.14)", transition: "border-color 0.18s ease, box-shadow 0.18s ease", "&:focus-within": { borderColor: "#106048", boxShadow: "0 0 0 4px rgba(16,96,72,0.10), 0 8px 24px -14px rgba(16,24,40,0.18)" } },
  input: { flex: 1, "& .MuiOutlinedInput-root": { padding: "6px 0 !important", border: "none !important" }, "& .MuiOutlinedInput-notchedOutline": { border: "none !important" }, "& .MuiInputBase-input": { fontSize: 14, fontFamily: "Montserrat", lineHeight: 1.5, padding: "4px 0 !important" }, "& .MuiInputBase-input::placeholder": { color: "#9ca3af" } },
  sendBtn: { width: 38, height: 38, background: "linear-gradient(135deg, #12855f 0%, #106048 100%) !important", borderRadius: "12px !important", color: "#fff !important", boxShadow: "0 4px 12px -4px rgba(16,96,72,0.5)", transition: "transform 0.14s ease, opacity 0.14s ease", "&:hover": { transform: "translateY(-1px) scale(1.03)" }, "&:disabled": { opacity: 0.3, boxShadow: "none", transform: "none" } },
  hint: { textAlign: "center", fontSize: 11, color: "#9ca3af", mt: "8px", fontFamily: "Montserrat" },
};
